// frontend/src/pages/UnsubscribeConfirmPage.tsx
//
// Public confirmation page shown after the unsubscribe Lambda processes a
// one-click unsubscribe request (#169).
//
// Reads ?status=success or ?status=invalid from the URL. The Lambda already
// did the DB work — this page is pure render. No API calls, no auth
// requirement (the user clicking the link from email is anonymous from the
// site's POV).
//
// Anything other than ?status=success renders as the invalid state, both
// for safety (don't claim success unless the Lambda explicitly said so) and
// for forward compatibility (future error codes can pass through without
// breaking the page).

import React from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Button,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

const TEAL = '#2D6E6A';
const TEAL_HOVER = '#009688';

const UnsubscribeConfirmPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isSuccess = searchParams.get('status') === 'success';

  const headline = isSuccess ? "You're unsubscribed" : "This link didn't work";
  const body = isSuccess
    ? "We've stopped sending you Percoro lifecycle emails. You can re-enable them anytime from your settings."
    : "The unsubscribe link may have expired or been altered. You can manage your email preferences directly in your settings.";
  const ctaLabel = isSuccess ? 'Manage preferences' : 'Go to settings';
  const Icon = isSuccess ? CheckCircleOutlineIcon : ErrorOutlineIcon;
  const iconColor = isSuccess ? TEAL : '#E57373';

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #f7fffe 0%, #f0faf8 100%)',
        px: 2,
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)' }}>
          <CardContent sx={{ p: { xs: 3, sm: 5 }, textAlign: 'center' }}>
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="h5"
                sx={{ fontWeight: 600, color: TEAL, mb: 0.5 }}
              >
                ✈️ Percoro
              </Typography>
            </Box>

            <Icon sx={{ fontSize: 56, color: iconColor, mb: 2 }} />

            <Typography
              variant="h5"
              component="h1"
              sx={{ fontWeight: 600, mb: 2, color: '#222' }}
            >
              {headline}
            </Typography>

            <Typography
              variant="body1"
              sx={{ color: '#555', mb: 4, lineHeight: 1.6 }}
            >
              {body}
            </Typography>

            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/settings#notifications')}
              sx={{
                borderColor: TEAL,
                color: TEAL,
                px: 4,
                py: 1.25,
                fontWeight: 600,
                textTransform: 'none',
                fontSize: '1rem',
                borderRadius: 2,
                '&:hover': {
                  borderColor: TEAL_HOVER,
                  bgcolor: 'rgba(0, 180, 160, 0.04)',
                },
              }}
            >
              {ctaLabel}
            </Button>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default UnsubscribeConfirmPage;
